/**
 * IframeBridge — PostMessage-based bridge for iframe embedding.
 *
 * Provides type-safe communication between Studio (host) and the preview
 * iframe. Use the static factories `IframeBridge.forHost()` and
 * `IframeBridge.forIframe()` to clarify direction.
 */

import { nanoid } from 'nanoid'
import type { IframeMessage, IframeMessageHandler } from './types'

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Type guard: checks whether `data` looks like a valid IframeMessage.
 */
export function isValidIframeMessage(data: unknown): data is IframeMessage {
  if (data === null || data === undefined) return false
  if (typeof data !== 'object') return false
  if (Array.isArray(data)) return false

  const record = data as Record<string, unknown>
  if (typeof record.type !== 'string') return false
  if (record.type.length === 0) return false

  return true
}

/**
 * Create a command message (request from host to iframe).
 * Commands carry an auto-generated `id` for request/response correlation.
 */
export function createIframeCommand(
  type: string,
  payload?: Record<string, unknown>,
): IframeMessage {
  const msg: IframeMessage = { type }
  msg.id = nanoid()
  if (payload !== undefined) {
    msg.payload = payload
  }
  return msg
}

/**
 * Create an event message (notification from iframe to host).
 * Events are fire-and-forget -- no `id`.
 */
export function createIframeEvent(
  type: string,
  payload?: Record<string, unknown>,
): IframeMessage {
  const msg: IframeMessage = { type }
  if (payload !== undefined) {
    msg.payload = payload
  }
  return msg
}

// ---------------------------------------------------------------------------
// IframeBridge
// ---------------------------------------------------------------------------

/**
 * Derive the origin a message exchange with `iframe` should be pinned to.
 * Returns null when the origin is opaque/underivable (about:blank, srcdoc
 * with opaque origin, invalid src) — callers fall back to '*'.
 */
export function deriveIframeOrigin(iframe: HTMLIFrameElement): string | null {
  try {
    const src = iframe.getAttribute('src')
    if (!src) return null
    const origin = new URL(src, typeof window !== 'undefined' ? window.location.href : undefined).origin
    return origin && origin !== 'null' ? origin : null
  } catch {
    return null
  }
}

export class IframeBridge {
  private listeners = new Map<string, Set<IframeMessageHandler>>()
  /** Inbound messages must come from this window (lazily resolved). When
   * set and the resolved window is null/mismatched, the message is dropped.
   * The preview iframe runs GENERATED app content inside an
   * allow-same-origin sandbox — without a source check, any window able to
   * post to us (a sibling preview, an opener, the previewed app itself
   * after navigation) could forge protocol events and spoof command
   * results. */
  private expectedSource: (() => Window | null) | null = null
  /** Origin pin for inbound validation + outbound targetOrigin. '*' = unpinned. */
  private pinnedOrigin: string
  /** Iframe side: the host origin learned from the first source-validated
   * inbound message — used to pin replies when no explicit origin was given. */
  private learnedHostOrigin: string | null = null

  private constructor(targetOrigin: string = '*') {
    this.pinnedOrigin = targetOrigin
    if (typeof window !== 'undefined') {
      window.addEventListener('message', this.handleMessage)
    }
  }

  /**
   * Create a bridge for use inside the iframe (preview).
   * `sendToHost()` posts to `window.parent`.
   * Inbound messages are accepted from the embedding parent only.
   */
  static forIframe(targetOrigin: string = '*'): IframeBridge {
    const bridge = new IframeBridge(targetOrigin)
    bridge.expectedSource = () => (typeof window !== 'undefined' ? window.parent : null)
    return bridge
  }

  /**
   * Create a bridge for use in the host (Studio).
   * `sendToIframe()` posts to the iframe's `contentWindow`.
   * Call `bindIframe()` once the iframe element exists so inbound messages
   * are accepted from that iframe only.
   */
  static forHost(targetOrigin: string = '*'): IframeBridge {
    return new IframeBridge(targetOrigin)
  }

  /**
   * Restrict this host-side bridge to a specific iframe: inbound messages
   * must originate from its contentWindow, and (when derivable from its
   * src) the exchange is pinned to the iframe's origin.
   */
  bindIframe(iframe: HTMLIFrameElement, options?: { origin?: string }): void {
    this.expectedSource = () => iframe.contentWindow
    if (this.pinnedOrigin === '*') {
      const origin = options?.origin ?? deriveIframeOrigin(iframe)
      if (origin) this.pinnedOrigin = origin
    }
  }

  /**
   * Send a message to the host window (window.parent).
   * Used from inside the iframe. Pinned to the explicit targetOrigin, or to
   * the host origin learned from the first validated inbound message.
   */
  sendToHost(message: IframeMessage): void {
    if (typeof window !== 'undefined') {
      const target = this.pinnedOrigin !== '*' ? this.pinnedOrigin : this.learnedHostOrigin ?? '*'
      window.parent.postMessage(message, target)
    }
  }

  /**
   * Send a message to a specific iframe element.
   * Used from the host (Studio). Pinned to the bridge origin, or derived
   * per-send from the iframe's src instead of broadcasting with '*'.
   */
  sendToIframe(iframe: HTMLIFrameElement, message: IframeMessage): void {
    if (iframe.contentWindow) {
      const target = this.pinnedOrigin !== '*' ? this.pinnedOrigin : deriveIframeOrigin(iframe) ?? '*'
      iframe.contentWindow.postMessage(message, target)
    }
  }

  /**
   * Register a handler for a given message type.
   * Returns an unsubscribe function.
   */
  on(type: string, handler: IframeMessageHandler): () => void {
    if (!this.listeners.has(type)) {
      this.listeners.set(type, new Set())
    }
    this.listeners.get(type)!.add(handler)

    return () => {
      this.listeners.get(type)?.delete(handler)
    }
  }

  /**
   * Internal message handler bound to window 'message' events.
   */
  private handleMessage = (event: MessageEvent): void => {
    if (this.expectedSource) {
      const source = this.expectedSource()
      if (!source || event.source !== source) return
    }
    if (this.pinnedOrigin !== '*' && event.origin !== this.pinnedOrigin) return
    if (!isValidIframeMessage(event.data)) return

    // Source-validated inbound — remember the counterpart origin so replies
    // can be pinned even when the bridge was created without one.
    if (this.learnedHostOrigin === null && event.origin && event.origin !== 'null') {
      this.learnedHostOrigin = event.origin
    }

    const msg = event.data as IframeMessage
    const handlers = this.listeners.get(msg.type)
    if (handlers) {
      handlers.forEach((fn) => fn(msg))
    }
  }

  /**
   * Remove the window listener and clear all handlers.
   */
  destroy(): void {
    if (typeof window !== 'undefined') {
      window.removeEventListener('message', this.handleMessage)
    }
    this.listeners.clear()
  }
}
